import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FunctionField, useNotify, useTranslate } from 'react-admin';
import {
  BbReward,
  BbValidateProbabilities,
  bbCreateReward,
  bbDeleteReward,
  bbGetPoolStocks,
  bbGetRewards,
  bbRefillPool,
  bbUpdateReward,
  bbValidateRewardProbabilities,
} from '../../../services/blindBoxService';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import { bannersUploadByServer } from '../../../services/bannersUpload';
import { bannersDataProvider } from '../../banners/dataProvider';
import { Resources } from '../../resource';
import {
  BB_REWARD_CATEGORY_BOX_DEFAULTS,
  BB_REWARD_CATEGORY_WHEEL_DEFAULTS,
  isPredictionTicket,
  validatePredictionTicketReward,
} from '../shared/rewardCategories';

// /blind-box/rewards routes require bb-lucky-wheel:<action> OR bb-probability:<action>
const REWARD_CREATE_PERMS = [
  { resource: 'bb-lucky-wheel', action: 'create' },
  { resource: 'bb-probability', action: 'create' },
];
const REWARD_UPDATE_PERMS = [
  { resource: 'bb-lucky-wheel', action: 'update' },
  { resource: 'bb-probability', action: 'update' },
];
const REWARD_DELETE_PERMS = [
  { resource: 'bb-lucky-wheel', action: 'delete' },
  { resource: 'bb-probability', action: 'delete' },
];

function probToPercent(prob?: string | number) {
  const n = typeof prob === 'number' ? prob : parseFloat(String(prob ?? ''));
  if (!Number.isFinite(n)) return '';
  // 保留最多 4 位小数的百分比（如 0.31 -> 31, 0.0031 -> 0.31）
  const v = n * 100;
  const s = v.toFixed(4);
  return s.replace(/\.?0+$/, '');
}

function percentToProb(percent?: string | number) {
  const n = typeof percent === 'number' ? percent : parseFloat(String(percent ?? ''));
  if (!Number.isFinite(n)) return '0';
  const v = n / 100;
  // 后端用 string 浮点，保留 8 位小数足够用于概率配置
  const s = v.toFixed(8);
  return s.replace(/\.?0+$/, '');
}

function RewardTable({
  sourceModule,
  rewards,
  refreshKey,
  onChanged,
}: {
  sourceModule: 'WHEEL' | 'BOX';
  rewards: BbReward[];
  refreshKey: number;
  onChanged: () => void;
}) {
  const t = useTranslate();
  const notify = useNotify();
  const list = useMemo(
    () => rewards.filter((r) => r.sourceModule === sourceModule),
    [rewards, sourceModule]
  );
  const rewardCategories = useMemo(() => {
    const defaults =
      sourceModule === 'WHEEL' ? BB_REWARD_CATEGORY_WHEEL_DEFAULTS : BB_REWARD_CATEGORY_BOX_DEFAULTS;
    const uniq = Array.from(new Set(list.map((r) => r.rewardCategory).filter(Boolean)));
    uniq.sort((a, b) => String(a).localeCompare(String(b)));
    const merged = Array.from(new Set([...defaults, ...uniq]));
    return merged;
  }, [list, sourceModule]);
  const [valid, setValid] = useState<BbValidateProbabilities | null>(null);
  const [dlg, setDlg] = useState(false);
  const [edit, setEdit] = useState<BbReward | null>(null);
  const [form, setForm] = useState<Partial<BbReward>>({});
  const isTicketReward = isPredictionTicket(edit?.rewardCategory ?? form.rewardCategory);
  const [refillDlg, setRefillDlg] = useState<{ id: number; amount: string } | null>(null);
  const iconFileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [uploadCfg, setUploadCfg] = useState<{
    uploadUrl?: string;
    fileType?: string;
    tags?: string;
    pathPrefix?: string;
  } | null>(null);
  const [remainingByRewardId, setRemainingByRewardId] = useState<Map<number, number>>(new Map());
  const [distributedByRewardId, setDistributedByRewardId] = useState<Map<number, number>>(
    new Map()
  );

  const loadPoolStocks = useCallback(() => {
    bbGetPoolStocks({ sourceModule })
      .then((rows) => {
        const remainingMap = new Map<number, number>();
        const distributedMap = new Map<number, number>();
        rows.forEach((row) => {
          remainingMap.set(row.rewardId, row.remaining);
          distributedMap.set(row.rewardId, row.distributed);
        });
        setRemainingByRewardId(remainingMap);
        setDistributedByRewardId(distributedMap);
      })
      .catch(() => {
        setRemainingByRewardId(new Map());
        setDistributedByRewardId(new Map());
      });
  }, [sourceModule]);

  useEffect(() => {
    loadPoolStocks();
  }, [loadPoolStocks, refreshKey]);

  useEffect(() => {
    const fetchUploadCfg = async () => {
      try {
        const cfg = await bannersDataProvider.getUploadCfg(Resources.banners);
        setUploadCfg(cfg);
      } catch (error) {
        console.error('Failed to fetch upload config:', error);
      }
    };
    fetchUploadCfg();
  }, []);

  const handleIconFileSelect = () => {
    iconFileInputRef.current?.click();
  };

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!uploadCfg) {
      notify('Upload config not loaded yet', { type: 'error' });
      return;
    }
    setUploadingIcon(true);
    try {
      const result = await bannersUploadByServer(file, {
        uploadUrl: uploadCfg.uploadUrl ?? '',
        fileType: uploadCfg.fileType,
        tags: uploadCfg.tags,
        pathPrefix: uploadCfg.pathPrefix,
        customFilename: file.name,
      });
      const uploadedUrl = result?.file?.publicURL || result?.publicURL;
      if (!uploadedUrl) {
        console.error('data structure returned by upload:', result);
        notify('upload failed image url not obtained', { type: 'error' });
        throw new Error('Upload succeeded but no url returned');
      }
      setForm((prev) => ({ ...prev, iconUrl: uploadedUrl }));
      notify('UPLOAD SUCCESSFUL', { type: 'success' });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'UPLOAD FAILED';
      notify(errorMessage, { type: 'error' });
    } finally {
      setUploadingIcon(false);
      if (iconFileInputRef.current) iconFileInputRef.current.value = '';
    }
  };

  const formatTotalPool = (totalPool?: number) => {
    if (totalPool === 0) return t('module.blindBox.totalPoolUnlimited');
    return totalPool ?? '-';
  };

  const totalPoolLabel = isTicketReward
    ? `${t('module.blindBox.totalPool')} (${t('module.blindBox.totalPoolTicketHint')})`
    : `${t('module.blindBox.totalPool')} (${t('module.blindBox.totalPoolUnlimitedHint')})`;

  const checkProb = useCallback(() => {
    bbValidateRewardProbabilities(sourceModule)
      .then(setValid)
      .catch(() => setValid(null));
  }, [sourceModule]);

  useEffect(() => {
    checkProb();
  }, [checkProb, list]);

  const openCreate = () => {
    setEdit(null);
    setForm({
      sourceModule,
      rewardCategory: 'CASH_USDC',
      name: '',
      amount: '0',
      baseProbability: '0',
      totalPool: 0,
      displayOrder: list.length,
      isRare:false,
      isActive: true,
    });
    setDlg(true);
  };

  const openEdit = (r: BbReward) => {
    setEdit(r);
    setForm({ ...r, baseProbability: probToPercent(r.baseProbability) });
    setDlg(true);
  };

  const saveReward = async () => {
    try {
      const ticketErr = validatePredictionTicketReward({
        sourceModule,
        rewardCategory: edit?.rewardCategory ?? form.rewardCategory,
        amount: form.amount,
        totalPool: form.totalPool,
        tokenType: edit?.tokenType ?? form.tokenType,
      });
      if (ticketErr) {
        notify(ticketErr, { type: 'warning' });
        return;
      }
      const baseProbability = percentToProb(form.baseProbability ?? '0');
      if (edit) {
        await bbUpdateReward(edit.id, {
          name: form.name,
          amount: form.amount,
          baseProbability,
          totalPool: form.totalPool,
          isActive: form.isActive,
          displayOrder: form.displayOrder,
          description: form.description,
          iconUrl: form.iconUrl,
          isRare: form.isRare,
          expression: form.expression,
        });
      } else {
        await bbCreateReward({
          sourceModule,
          rewardCategory: form.rewardCategory,
          name: form.name!,
          amount: form.amount!,
          baseProbability,
          totalPool: form.totalPool!,
          displayOrder: form.displayOrder!,
          description: form.description,
          iconUrl: form.iconUrl,
          isRare: form.isRare,
          expression: form.expression,
        });
      }
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setDlg(false);
      onChanged();
      checkProb();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm(t('module.blindBox.confirmDelete'))) return;
    try {
      await bbDeleteReward(id);
      notify(t('module.blindBox.deleteOk'), { type: 'success' });
      onChanged();
      checkProb();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  const doRefill = async () => {
    if (!refillDlg) return;
    try {
      await bbRefillPool(refillDlg.id, parseInt(refillDlg.amount, 10));
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setRefillDlg(null);
      onChanged();
      loadPoolStocks();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  const toggleActive = async (form: any, next: boolean) => {
    try {
      await bbUpdateReward(form.id, {
        name: form.name,
        amount: form.amount,
        baseProbability: String(form.baseProbability ?? '0'),
        totalPool: form.totalPool,
        isActive: next,
        displayOrder: form.displayOrder,
        description: form.description,
        iconUrl: form.iconUrl,
        expression: form.expression,
      });
      setDlg(false);
      onChanged();
      checkProb();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  }
  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <PermissionGuard anyOf={REWARD_CREATE_PERMS} fallback={null} loadingFallback={null}>
          <Button startIcon={<AddIcon />} variant="outlined" onClick={openCreate}>
            {t('module.blindBox.addReward')}
          </Button>
        </PermissionGuard>
        <Button variant="text" onClick={checkProb}>
          {t('module.blindBox.validateProb')}
        </Button>
      </Stack>
      {valid && (
        <Alert severity={valid.isValid ? 'success' : 'warning'} sx={{ mb: 2 }}>
          {t('module.blindBox.probSum')}: {probToPercent(valid.total)}% —{' '}
          {valid.isValid ? 'OK' : t('module.blindBox.probInvalid')}
        </Alert>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>{t('module.blindBox.colName')}</TableCell>
              <TableCell>{t('module.blindBox.colCategory')}</TableCell>
              <TableCell>{t('module.blindBox.colAmount')}</TableCell>
              {/*{sourceModule !== 'BOX' && <TableCell>{t('module.blindBox.colProb')}</TableCell>}*/}
              {/*<TableCell>{t('module.blindBox.colPool')}</TableCell>*/}
              {/*<TableCell>{t('module.blindBox.colActive')}</TableCell>*/}
              <TableCell>{t('module.blindBox.isRare')}</TableCell>
              <TableCell>{t('module.blindBox.colRemaining')}</TableCell>
              <TableCell>{t('module.blindBox.distributed')}</TableCell>
              <TableCell>{t('module.blindBox.totalPool')}</TableCell>
              <TableCell>{t('module.blindBox.colProb')}</TableCell>
              <TableCell>{t('module.blindBox.iconUrl')}</TableCell>

              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map(
              (r) =>
                r.isActive && (
                  <TableRow key={r.id}>
                    <TableCell>{r.id}</TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{r.rewardCategory}</TableCell>
                    <TableCell>{r.amount}</TableCell>
                    <TableCell>{r?.isRare ? t('common.yes') : t('common.no')}</TableCell>
                    <TableCell>
                      {remainingByRewardId.has(r.id) ? remainingByRewardId.get(r.id) : '-'}
                    </TableCell>
                    <TableCell>
                      {r.totalPool === 0
                        ? '-'
                        : distributedByRewardId.has(r.id)
                          ? distributedByRewardId.get(r.id)
                          : '-'}
                    </TableCell>
                    <TableCell>{formatTotalPool(r?.totalPool)}</TableCell>
                    {/*{sourceModule !== 'BOX' && (*/}
                    <TableCell>{`${probToPercent(r.baseProbability)}%`}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 100,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        if (r?.iconUrl) window.open(r.iconUrl, '_blank');
                      }}
                      title={r?.iconUrl}
                    >
                      {/* 显示图片缩略图 */}
                      {r?.iconUrl ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img
                            src={r.iconUrl}
                            alt="icon"
                            style={{ width: 32, height: 32, objectFit: 'contain' }}
                          />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {r.iconUrl}
                          </span>
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    {/*)}*/}
                    <TableCell align="right">
                      <PermissionGuard
                        anyOf={REWARD_UPDATE_PERMS}
                        fallback={null}
                        loadingFallback={null}
                      >
                        <IconButton size="small" onClick={() => openEdit(r)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setRefillDlg({ id: r.id, amount: '1' })}
                        >
                          <Typography variant="caption">{t('module.blindBox.refill')}</Typography>
                        </IconButton>
                      </PermissionGuard>
                      <PermissionGuard
                        anyOf={REWARD_DELETE_PERMS}
                        fallback={null}
                        loadingFallback={null}
                      >
                        <IconButton size="small" onClick={() => remove(r.id)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </PermissionGuard>
                    </TableCell>
                  </TableRow>
                )
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dlg} onClose={() => setDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {edit ? t('module.blindBox.editReward') : t('module.blindBox.addReward')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!edit && (
              <TextField
                select
                label={t('module.blindBox.colCategory')}
                value={form.rewardCategory || 'CASH_USDC'}
                onChange={(e) => setForm({ ...form, rewardCategory: e.target.value })}
                fullWidth
              >
                {rewardCategories.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label={t('module.blindBox.colName')}
              value={form.name || ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
            />
            <TextField
              label={
                isTicketReward
                  ? `${t('module.blindBox.colAmount')} (ticket count)`
                  : t('module.blindBox.colAmount')
              }
              value={form.amount || ''}
              onChange={(e) => {
                const val = e.target.value;
                if (isTicketReward) {
                  if (val === '' || /^\d+$/.test(val)) {
                    setForm({ ...form, amount: val });
                  }
                  return;
                }
                setForm({ ...form, amount: val });
              }}
              type={isTicketReward ? 'number' : 'text'}
              inputProps={isTicketReward ? { min: 1, step: 1 } : undefined}
              fullWidth
            />
            <TextField
              label={t('module.blindBox.colExpression')}
              value={form.expression ?? ''}
              onChange={(e) => setForm({ ...form, expression: e.target.value })}
              placeholder={t('module.blindBox.colExpressionHint')}
              fullWidth
            />
            <TextField
              label={totalPoolLabel}
              type="number"
              value={form.totalPool || 0}
              onChange={(e) => {
                const val = e.target.value;
                setForm({
                  ...form,
                  totalPool: val === '' ? 0 : Number(val),
                });
              }}
              inputProps={isTicketReward ? { min: 1, step: 1 } : undefined}
              fullWidth
            />
            <TextField
              label={t('module.blindBox.colProb')}
              type="number"
              value={form.baseProbability ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                // 输入 0~100（百分比）
                if (v === '') return setForm({ ...form, baseProbability: '' });
                const n = Math.max(0, Math.min(100, parseFloat(v)));
                const s = Number.isFinite(n) ? String(n) : '';
                setForm({ ...form, baseProbability: s });
              }}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              fullWidth
            />
            <FormControlLabel
              label={t('module.blindBox.isRare')}
              control={
                <Switch
                  checked={form.isRare}
                  onChange={(e) => setForm({ ...form, isRare: e.target.checked })}
                />
              }
              sx={{ flex: 1 }}
            />
            {edit && (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                }
                label={t('module.blindBox.colActive')}
              />
            )}
            <Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
                <TextField
                  label="iconUrl"
                  value={form.iconUrl || ''}
                  onChange={(e) => setForm({ ...form, iconUrl: e.target.value })}
                  fullWidth
                  sx={{ flex: 1 }}
                />
                <input
                  ref={iconFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/svg"
                  style={{ display: 'none' }}
                  onChange={handleIconUpload}
                />
                <Button
                  variant="outlined"
                  onClick={handleIconFileSelect}
                  disabled={uploadingIcon}
                  sx={{ flexShrink: 0 }}
                >
                  {uploadingIcon ? <CircularProgress size={18} /> : t('module.banners.upload')}
                </Button>
              </Box>
              {form.iconUrl ? (
                <Box
                  component="img"
                  src={form.iconUrl}
                  alt=""
                  sx={{ mt: 1, maxWidth: 80, maxHeight: 80, objectFit: 'contain' }}
                />
              ) : null}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(false)}>{t('actions.cancel')}</Button>
          <PermissionGuard
            anyOf={edit ? REWARD_UPDATE_PERMS : REWARD_CREATE_PERMS}
            fallback={null}
            loadingFallback={null}
          >
            <Button variant="contained" onClick={saveReward}>
              {t('actions.save')}
            </Button>
          </PermissionGuard>
        </DialogActions>
      </Dialog>

      <Dialog open={!!refillDlg} onClose={() => setRefillDlg(null)}>
        <DialogTitle>{t('module.blindBox.refill')}</DialogTitle>
        <DialogContent>
          <TextField
            type="number"
            label={t('module.blindBox.refillAmount')}
            value={refillDlg?.amount || ''}
            onChange={(e) => setRefillDlg((p) => (p ? { ...p, amount: e.target.value } : p))}
            fullWidth
            sx={{ mt: 1, minWidth: 280 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefillDlg(null)}>{t('actions.cancel')}</Button>
          <PermissionGuard anyOf={REWARD_UPDATE_PERMS} fallback={null} loadingFallback={null}>
            <Button variant="contained" onClick={doRefill}>
              {t('actions.save')}
            </Button>
          </PermissionGuard>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export function SectionRewards({
  refreshKey,
  modules = ['WHEEL', 'BOX'],
}: {
  refreshKey: number;
  modules?: ('WHEEL' | 'BOX')[];
}) {
  const t = useTranslate();
  const [rewards, setRewards] = useState<BbReward[]>([]);
  const load = useCallback(() => {
    bbGetRewards()
      .then(setRewards)
      .catch(() => setRewards([]));
  }, []);
  useEffect(() => {
    load();
  }, [load, refreshKey]);
  return (
    <Stack spacing={3}>
      {modules.includes('WHEEL') && (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t('module.blindBox.wheelRewards')}
          </Typography>
          <RewardTable
            sourceModule="WHEEL"
            rewards={rewards}
            refreshKey={refreshKey}
            onChanged={load}
          />
        </Box>
      )}
      {modules.includes('BOX') && (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t('module.blindBox.boxRewards')}
          </Typography>
          <RewardTable
            sourceModule="BOX"
            rewards={rewards}
            refreshKey={refreshKey}
            onChanged={load}
          />
        </Box>
      )}
    </Stack>
  );
}
